import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Undo,
  Redo,
  Image as ImageIcon,
  Eye,
  Variable,
  LayoutTemplate,
  Mail,
  Send,
  Monitor,
  Smartphone,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Lock,
  Unlock,
  Shield,
  Edit3,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== EMAIL-SAFE HTML SANITIZER ====================
const sanitizeForEmail = (html) => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Base styles for email clients - these are APPENDED to existing styles, not prepended
  // This ensures user-defined styles take priority
  const styleDefaults = {
    'p': 'font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #333333;',
    'strong': 'font-weight: bold;',
    'b': 'font-weight: bold;',
    'em': 'font-style: italic;',
    'i': 'font-style: italic;',
    'u': 'text-decoration: underline;',
    's': 'text-decoration: line-through;',
    'a': 'color: #0066cc; text-decoration: none;',
    'ul': 'margin: 8px 0; padding-left: 20px;',
    'ol': 'margin: 8px 0; padding-left: 20px;',
    'li': 'margin: 4px 0;',
    // NOTE: img styles intentionally minimal - do NOT add display:block or max-width:100%
    // as these break side-by-side table layouts. User must control image sizing explicitly.
    'img': '',
    'table': 'border-collapse: collapse; border-spacing: 0;',
    'td': 'vertical-align: top;',
    'div': 'font-family: Arial, sans-serif; font-size: 14px; color: #333333;',
  };
  
  const unsupportedTags = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea'];
  unsupportedTags.forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => el.remove());
  });
  
  const processElement = (element) => {
    if (element.nodeType !== 1) return;
    const tagName = element.tagName.toLowerCase();
    let existingStyle = element.getAttribute('style') || '';
    const defaultStyle = styleDefaults[tagName] || '';
    
    // IMPORTANT: User's existing styles come FIRST (higher priority)
    // Default styles only fill in gaps, they don't override
    if (existingStyle || defaultStyle) {
      // Only add default styles if the element doesn't already have inline styles
      // This preserves table widths, image dimensions, and layout styles
      const finalStyle = existingStyle ? existingStyle : defaultStyle;
      element.setAttribute('style', finalStyle.trim());
    }
    
    // Remove class/id but KEEP width/height/align attributes for email compatibility
    ['class', 'id', 'data-text-align'].forEach(attr => element.removeAttribute(attr));
    Array.from(element.children).forEach(child => processElement(child));
  };
  
  processElement(doc.body);
  return doc.body.innerHTML;
};

const validateEmailHtml = (html) => {
  const issues = [];
  if (!html || html.trim() === '') return { valid: true, issues: [] };
  if (html.includes('<link') && html.includes('stylesheet')) issues.push('External stylesheets not supported');
  if (html.includes('<script') || html.includes('javascript:')) issues.push('JavaScript not supported');
  if (html.includes('<iframe')) issues.push('Iframes not supported');
  if (html.includes('position:') && (html.includes('absolute') || html.includes('fixed'))) issues.push('Position absolute/fixed may not render');
  if (html.includes('display:') && (html.includes('flex') || html.includes('grid'))) issues.push('Flexbox/Grid may not work in Outlook');
  return { valid: issues.length === 0, issues };
};

// ==================== PROTECTED TEMPLATES ====================
// Templates with LOCKED branding and EDITABLE user sections
const PROTECTED_TEMPLATES = {
  alpine_professional: {
    id: 'alpine_professional',
    name: 'Alpine Armour Professional',
    description: 'Official brand signature with logo',
    category: 'Brand Standard',
    // Editable fields - users can only modify these
    editableFields: [
      { key: 'user_signoff', label: 'Sign-off (e.g. Kind regards)', placeholder: 'Kind regards', required: false },
      { key: 'user_firstname', label: 'First Name (for sign-off)', placeholder: 'Scott', required: false },
      { key: 'logo_url', label: 'Logo URL', placeholder: 'https://example.com/logo.png', required: true },
      { key: 'logo_size', label: 'Logo Size (px)', placeholder: '120', required: false },
      { key: 'business_names', label: 'Business Names (top line)', placeholder: 'Alpine Armour / Scott Liston Design', required: true },
      { key: 'user_title', label: 'Tagline', placeholder: 'Design, uniforms & merchandise', required: false },
      { key: 'user_phone', label: 'Phone', placeholder: '0400 909 119', required: true },
      { key: 'user_email', label: 'Email', placeholder: 'info@alpinearmour.com.au', required: true },
      { key: 'user_website', label: 'Website', placeholder: 'alpinearmour.com.au', required: false },
      { key: 'user_address', label: 'Address', placeholder: '1/5 Nicholson Street, Porepunkah VIC 3740', required: false },
    ],
    // Locked template HTML - structure cannot be changed
    // Uses table layout with logo LEFT, content RIGHT
    // Mobile responsive: scales down proportionally while maintaining structure
    // Tighter line spacing (6px instead of 12px) for P/E/W/A rows
    lockedHtml: `{{#if user_signoff}}<p style="font-family: Arial, sans-serif; font-size: 14px; color: #333333; margin: 0 0 2px 0;">{{user_signoff}}</p>{{/if}}{{#if user_firstname}}<p style="font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; color: #333333; margin: 0 0 12px 0;">{{user_firstname}}</p>{{/if}}<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; border-collapse: collapse; max-width: 100%;">
  <tr>
    <td valign="top" style="padding-right: 16px; vertical-align: top;">
      <img src="{{logo_url}}" alt="Logo" width="{{logo_size}}" height="{{logo_size}}" style="display: block; width: {{logo_size}}px; height: {{logo_size}}px;" />
    </td>
    <td valign="top" style="vertical-align: top; padding-left: 0;">
      <table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif;">
        <tr>
          <td style="padding-bottom: 2px;">
            <span style="font-size: 18px; font-weight: bold; color: #333333;">{{business_names}}</span>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 8px;">
            <span style="font-size: 13px; color: #666666;">{{user_title}}</span>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 10px;">
            <table cellpadding="0" cellspacing="0" border="0"><tr><td style="width: 180px; height: 2px; background-color: #00838f; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 6px; font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">P</span>&nbsp;&nbsp;<span style="color: #333333;">{{user_phone}}</span>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 6px; font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">E</span>&nbsp;&nbsp;<a href="mailto:{{user_email}}" style="color: #333333; text-decoration: none;">{{user_email}}</a>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 6px; font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">W</span>&nbsp;&nbsp;<a href="https://{{user_website}}" style="color: #333333; text-decoration: none;">{{user_website}}</a>
          </td>
        </tr>
        <tr>
          <td style="font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">A</span>&nbsp;&nbsp;<span style="color: #333333;">{{user_address}}</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`,
  },
  alpine_minimal: {
    id: 'alpine_minimal',
    name: 'Alpine Armour Minimal',
    description: 'Clean text-based signature',
    category: 'Brand Standard',
    editableFields: [
      { key: 'user_name', label: 'Your Name', placeholder: 'John Smith', required: true },
      { key: 'user_title', label: 'Job Title', placeholder: 'Sales Manager', required: false },
      { key: 'user_phone', label: 'Phone', placeholder: '+61 400 000 000', required: true },
      { key: 'user_email', label: 'Email', placeholder: 'you@alpinearmour.com.au', required: true },
    ],
    lockedHtml: `<table cellpadding="0" cellspacing="0" style="font-family: Arial, sans-serif; font-size: 13px; color: #333333;">
  <tr>
    <td>
      <p style="margin: 0; font-weight: bold; font-size: 14px; color: #1a5276;">{{user_name}}</p>
      <p style="margin: 2px 0 8px 0; color: #666666;">{{user_title}} | Alpine Armour</p>
      <p style="margin: 0; font-size: 12px;">
        <a href="tel:{{user_phone}}" style="color: #333333; text-decoration: none;">{{user_phone}}</a>
        <span style="color: #cccccc; margin: 0 6px;">|</span>
        <a href="mailto:{{user_email}}" style="color: #1a5276; text-decoration: none;">{{user_email}}</a>
      </p>
    </td>
  </tr>
</table>`,
  },
  alpine_sales: {
    id: 'alpine_sales',
    name: 'Alpine Armour Sales',
    description: 'Sales-focused with CTA button',
    category: 'Sales',
    editableFields: [
      { key: 'user_name', label: 'Your Name', placeholder: 'John Smith', required: true },
      { key: 'user_title', label: 'Job Title', placeholder: 'Sales Representative', required: false },
      { key: 'user_phone', label: 'Phone', placeholder: '+61 400 000 000', required: true },
      { key: 'user_email', label: 'Email', placeholder: 'you@alpinearmour.com.au', required: true },
      { key: 'user_cta', label: 'CTA Button Text', placeholder: 'Get a Quote', required: true },
      { key: 'user_cta_url', label: 'CTA Link', placeholder: 'https://alpinearmour.com.au/quote', required: true },
    ],
    lockedHtml: `<table cellpadding="0" cellspacing="0" style="font-family: Arial, sans-serif; font-size: 14px; color: #333333; max-width: 400px;">
  <tr>
    <td style="padding-bottom: 10px;">
      <img src="{{logo_url}}" alt="Alpine Armour" style="width: 100px; height: auto;" />
    </td>
  </tr>
  <tr>
    <td>
      <p style="margin: 0; font-weight: bold; font-size: 16px;">{{user_name}}</p>
      <p style="margin: 4px 0 10px 0; color: #666666;">{{user_title}}</p>
      <p style="margin: 0; font-size: 13px;">
        <a href="mailto:{{user_email}}" style="color: #1a5276; text-decoration: none;">{{user_email}}</a>
        <span style="color: #cccccc; margin: 0 8px;">|</span>
        {{user_phone}}
      </p>
      <p style="margin: 14px 0 0 0;">
        <a href="{{user_cta_url}}" style="display: inline-block; padding: 10px 24px; background-color: #e67e22; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: bold; font-size: 13px;">{{user_cta}}</a>
      </p>
    </td>
  </tr>
</table>`,
  },
  custom_free: {
    id: 'custom_free',
    name: 'Custom (Unlocked)',
    description: 'Full editing freedom - no restrictions',
    category: 'Custom',
    editableFields: [], // No restricted fields - full editing
    lockedHtml: '', // No locked content
    isUnlocked: true,
  },
};

// Legacy templates for backwards compatibility
const LEGACY_TEMPLATES = {
  professional: {
    name: 'Professional',
    description: 'Logo left, content right - Alpine Armour style',
    html: `<p style="font-family: Arial, sans-serif; font-size: 14px; color: #333333; margin: 0 0 2px 0;">Kind regards</p><p style="font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; color: #333333; margin: 0 0 12px 0;">{{name}}</p><table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; border-collapse: collapse; max-width: 100%;">
  <tr>
    <td valign="top" style="padding-right: 16px; vertical-align: top;">
      <img src="{{logo_url}}" alt="Logo" width="120" height="120" style="display: block; width: 120px; height: 120px;" />
    </td>
    <td valign="top" style="vertical-align: top; padding-left: 0;">
      <table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif;">
        <tr>
          <td style="padding-bottom: 2px;">
            <span style="font-size: 18px; font-weight: bold; color: #333333;">{{company}}</span>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 8px;">
            <span style="font-size: 13px; color: #666666;">{{title}}</span>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 10px;">
            <table cellpadding="0" cellspacing="0" border="0"><tr><td style="width: 180px; height: 2px; background-color: #00838f; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 6px; font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">P</span>&nbsp;&nbsp;<span style="color: #333333;">{{phone}}</span>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 6px; font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">E</span>&nbsp;&nbsp;<a href="mailto:{{email}}" style="color: #333333; text-decoration: none;">{{email}}</a>
          </td>
        </tr>
        <tr>
          <td style="padding-bottom: 6px; font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">W</span>&nbsp;&nbsp;<a href="https://{{website}}" style="color: #333333; text-decoration: none;">{{website}}</a>
          </td>
        </tr>
        <tr>
          <td style="font-size: 14px; line-height: 1.3;">
            <span style="color: #00838f; font-weight: bold;">A</span>&nbsp;&nbsp;<span style="color: #333333;">{{address}}</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`,
  },
  minimal: {
    name: 'Minimal',
    description: 'Simple text-based signature',
    html: `<p style="font-family: Arial, sans-serif; font-size: 14px; color: #333333; margin: 0;">
  <strong style="font-weight: bold;">{{name}}</strong><br />
  <span style="color: #666666;">{{title}}</span><br />
  <a href="mailto:{{email}}" style="color: #0066cc; text-decoration: none;">{{email}}</a> | {{phone}}
</p>`,
  },
};

const SIGNATURE_VARIABLES = [
  { key: '{{name}}', label: 'Full Name' },
  { key: '{{email}}', label: 'Email' },
  { key: '{{phone}}', label: 'Phone' },
  { key: '{{title}}', label: 'Job Title / Tagline' },
  { key: '{{company}}', label: 'Company' },
  { key: '{{website}}', label: 'Website' },
  { key: '{{address}}', label: 'Address' },
  { key: '{{logo_url}}', label: 'Logo URL' },
];

// ==================== TOOLBAR BUTTON ====================
const ToolbarButton = ({ onClick, isActive, children, title, disabled }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isActive ? 'bg-gray-200 text-blue-600' : 'text-gray-600'
          }`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">{title}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// ==================== MODE TOGGLE ====================
const ModeToggle = ({ mode, onModeChange, disabled }) => (
  <div className="flex items-center bg-gray-100 rounded-md p-0.5">
    <button
      type="button"
      onClick={() => onModeChange('wysiwyg')}
      disabled={disabled}
      className={`px-3 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${
        mode === 'wysiwyg' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      WYSIWYG
    </button>
    <button
      type="button"
      onClick={() => onModeChange('html')}
      disabled={disabled}
      className={`px-3 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${
        mode === 'html' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      HTML
    </button>
  </div>
);

// ==================== PREVIEW MODE TOGGLE ====================
const PreviewModeToggle = ({ previewMode, onModeChange }) => (
  <div className="flex items-center gap-1">
    <button
      type="button"
      onClick={() => onModeChange('desktop')}
      className={`p-1.5 rounded transition-colors ${
        previewMode === 'desktop' ? 'bg-gray-200 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }`}
      title="Desktop Preview"
    >
      <Monitor className="w-4 h-4" />
    </button>
    <button
      type="button"
      onClick={() => onModeChange('mobile')}
      className={`p-1.5 rounded transition-colors ${
        previewMode === 'mobile' ? 'bg-gray-200 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }`}
      title="Mobile Preview"
    >
      <Smartphone className="w-4 h-4" />
    </button>
  </div>
);

// ==================== EMAIL PREVIEW ====================
const EmailPreview = ({ html, variables = {}, previewMode = 'desktop' }) => {
  const previewHtml = useMemo(() => {
    let processed = html || '';
    const previewValues = {
      '{{name}}': variables.name || 'Scott',
      '{{user_name}}': variables.user_name || variables.name || 'Scott',
      '{{user_firstname}}': variables.user_firstname || 'Scott',
      '{{user_signoff}}': variables.user_signoff || 'Kind regards',
      '{{business_names}}': variables.business_names || 'Alpine Armour / Scott Liston Design',
      '{{email}}': variables.email || 'info@alpinearmour.com.au',
      '{{user_email}}': variables.user_email || variables.email || 'info@alpinearmour.com.au',
      '{{phone}}': variables.phone || '0400 909 119',
      '{{user_phone}}': variables.user_phone || variables.phone || '0400 909 119',
      '{{title}}': variables.title || 'Design, uniforms & merchandise',
      '{{user_title}}': variables.user_title || variables.title || 'Design, uniforms & merchandise',
      '{{company}}': variables.company || 'Alpine Armour / Scott Liston Design',
      '{{website}}': variables.website || 'alpinearmour.com.au',
      '{{user_website}}': variables.user_website || variables.website || 'alpinearmour.com.au',
      '{{address}}': variables.address || '1/5 Nicholson Street, Porepunkah VIC 3740',
      '{{user_address}}': variables.user_address || variables.address || '1/5 Nicholson Street, Porepunkah VIC 3740',
      '{{logo_url}}': variables.logo_url || 'https://via.placeholder.com/140x140?text=Logo',
      '{{user_cta}}': variables.user_cta || 'Get Started',
      '{{user_cta_url}}': variables.user_cta_url || '#',
    };
    
    // Handle conditional blocks {{#if field}}...{{/if}}
    processed = processed.replace(/\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, field, content) => {
      const value = previewValues[`{{${field}}}`];
      return value && value !== '' && value !== '#' ? content : '';
    });
    
    Object.entries(previewValues).forEach(([key, value]) => {
      processed = processed.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return processed;
  }, [html, variables]);

  const validation = useMemo(() => validateEmailHtml(html), [html]);
  const containerWidth = previewMode === 'mobile' ? '375px' : '100%';
  
  // Preview styles - minimal reset, do NOT force responsive image sizing
  // This ensures the preview renders exactly what the user's HTML specifies
  // Mobile mode: add scaling transform to shrink the signature proportionally
  const mobileScaleStyle = previewMode === 'mobile' 
    ? `body { transform: scale(0.85); transform-origin: top left; width: 117%; }` 
    : '';
  const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{font-family:Arial,sans-serif;font-size:14px;color:#333;margin:0;padding:16px;background:#fff}a{color:#0066cc}table{border-collapse:collapse}${mobileScaleStyle}</style></head><body>${previewHtml}</body></html>`;

  return (
    <div className="border rounded-md bg-white overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b flex items-center gap-2">
        <Eye className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs font-medium text-gray-500">Live Preview</span>
        <div className="flex-1" />
        <PreviewModeToggle previewMode={previewMode} onModeChange={() => {}} />
        {validation.valid ? (
          <Badge variant="outline" className="text-[10px] text-green-600 border-green-200 bg-green-50">
            <CheckCircle className="w-3 h-3 mr-1" />Email Safe
          </Badge>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50">
                  <AlertTriangle className="w-3 h-3 mr-1" />{validation.issues.length} Issue{validation.issues.length > 1 ? 's' : ''}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <ul className="text-xs space-y-1">{validation.issues.map((issue, i) => <li key={i}>• {issue}</li>)}</ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className={`flex justify-center bg-gray-100 p-4 ${previewMode === 'mobile' ? 'bg-gray-200' : ''}`}>
        <div style={{ width: containerWidth, maxWidth: '100%' }} className={`bg-white ${previewMode === 'mobile' ? 'shadow-lg rounded-lg' : ''}`}>
          <iframe srcDoc={fullHtml} className={`w-full border-0 ${previewMode === 'mobile' ? 'h-[300px]' : 'h-[200px]'}`} title="Signature Preview" sandbox="allow-same-origin" />
        </div>
      </div>
    </div>
  );
};

// ==================== PROTECTED TEMPLATE EDITOR ====================
// Allows editing only specific fields while keeping template structure locked
const ProtectedTemplateEditor = ({ 
  template, 
  fieldValues, 
  onFieldChange, 
  onUnlock,
  businessSettings = {},
}) => {
  if (!template) return null;
  
  return (
    <div className="space-y-4">
      {/* Locked indicator */}
      <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
        <Shield className="w-4 h-4 text-blue-600" />
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-900">Template: {template.name}</p>
          <p className="text-xs text-blue-600">Brand layout is protected • Edit your details below</p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" onClick={onUnlock} className="h-7 text-xs text-blue-600 hover:text-blue-700">
                <Unlock className="w-3 h-3 mr-1" />
                Full Edit
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Switch to full HTML editing mode</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Editable fields */}
      <div className="grid gap-3">
        {template.editableFields.map((field) => (
          <div key={field.key} className="grid gap-1.5">
            <Label className="text-xs font-medium text-gray-700 flex items-center gap-1">
              <Edit3 className="w-3 h-3 text-gray-400" />
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={fieldValues[field.key] || ''}
              onChange={(e) => onFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="h-9 text-sm"
            />
          </div>
        ))}
      </div>

      {/* Locked structure indicator */}
      <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Lock className="w-3 h-3" />
          <span>Logo, layout, and brand colors are locked to maintain consistency</span>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function EmailSignatureEditor({ 
  value, 
  onChange, 
  email,
  showPreview = true,
  previewVariables = {},
  businessSettings = {},
  className = '',
}) {
  const [mode, setMode] = useState('wysiwyg');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [htmlSource, setHtmlSource] = useState(value || '');
  const [isSendingTest, setIsSendingTest] = useState(false);
  
  // Template protection state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFieldValues, setTemplateFieldValues] = useState({});
  const [isProtectedMode, setIsProtectedMode] = useState(false);
  const [showUnlockWarning, setShowUnlockWarning] = useState(false);
  
  // Ref to track last generated HTML to prevent infinite loops
  const lastGeneratedHtmlRef = useRef('');
  // Ref for onChange to avoid dependency issues
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  
  // Merge business settings with preview variables
  const mergedVariables = useMemo(() => ({
    name: businessSettings.business_name || previewVariables.name || 'Scott',
    email: email || previewVariables.email || 'your@email.com',
    phone: businessSettings.business_phone || previewVariables.phone || '0400 909 119',
    title: previewVariables.title || 'Design, uniforms & merchandise',
    company: businessSettings.business_name || previewVariables.company || 'Alpine Armour / Scott Liston Design',
    website: businessSettings.business_website || previewVariables.website || 'alpinearmour.com.au',
    logo_url: businessSettings.email_logo_url || businessSettings.business_logo_url || previewVariables.logo_url,
    ...templateFieldValues,
  }), [businessSettings, previewVariables, email, templateFieldValues]);

  // Generate HTML from protected template
  const generateTemplateHtml = useCallback((template, values) => {
    if (!template || !template.lockedHtml) return '';
    
    let html = template.lockedHtml;
    
    // Set default logo size if not provided
    const logoSize = values.logo_size || '120';
    const valuesWithDefaults = { ...values, logo_size: logoSize };
    
    // Replace template fields first
    Object.entries(valuesWithDefaults).forEach(([key, val]) => {
      html = html.replace(new RegExp(`{{${key}}}`, 'g'), val || '');
    });
    
    // Replace logo_url - use email logo, then PDF logo, then template field value, then placeholder
    const logoUrl = values.logo_url || businessSettings.email_logo_url || businessSettings.business_logo_url || `https://via.placeholder.com/${logoSize}x${logoSize}?text=Logo`;
    html = html.replace(/\{\{logo_url\}\}/g, logoUrl);
    
    // Handle conditional blocks
    html = html.replace(/\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, field, content) => {
      const fieldValue = valuesWithDefaults[field];
      return fieldValue && fieldValue.trim() !== '' ? content : '';
    });
    
    return html;
  }, [businessSettings]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { style: 'color: #0066cc; text-decoration: none;' } }),
      TextAlign.configure({ types: ['paragraph'] }),
      // Image extension - do NOT force max-width, let the HTML control sizing for table layouts
      Image.configure({ inline: true }),
      // Table extensions for email signature layouts (logo left, content right)
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: value || '',
    editable: !isProtectedMode,
    onUpdate: ({ editor }) => {
      if (!isProtectedMode) {
        const html = editor.getHTML();
        setHtmlSource(html);
        onChange(sanitizeForEmail(html));
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[150px] p-3 focus:outline-none',
        style: 'font-family: Arial, sans-serif; font-size: 14px;',
      },
    },
  });

  // Update editor editability when protected mode changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isProtectedMode);
    }
  }, [isProtectedMode, editor]);

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !isProtectedMode) {
      // Don't push table-based HTML into TipTap - it transforms the structure
      // Keep table HTML in htmlSource directly for HTML mode editing
      if (value && value.includes('<table')) {
        setHtmlSource(value);
        setMode('html'); // Switch to HTML mode to preserve table layout
      } else {
        editor.commands.setContent(value || '');
        setHtmlSource(value || '');
      }
    }
  }, [value, editor, isProtectedMode]);

  // When template field values change, regenerate HTML
  useEffect(() => {
    if (isProtectedMode && selectedTemplate) {
      const html = generateTemplateHtml(selectedTemplate, templateFieldValues);
      // Only update if HTML actually changed to prevent infinite loops
      if (html !== lastGeneratedHtmlRef.current) {
        lastGeneratedHtmlRef.current = html;
        setHtmlSource(html);
        onChangeRef.current(html);
      }
    }
  }, [templateFieldValues, isProtectedMode, selectedTemplate, generateTemplateHtml]);

  // Handle mode change
  const handleModeChange = (newMode) => {
    if (isProtectedMode) return; // Don't allow mode change in protected mode
    if (newMode === 'wysiwyg' && mode === 'html') {
      // Warn if HTML contains tables - they may not render correctly in WYSIWYG
      if (htmlSource.includes('<table')) {
        toast.warning('Table layouts may change in WYSIWYG mode', {
          description: 'For best results with table-based signatures, stay in HTML mode'
        });
      }
      if (editor) editor.commands.setContent(htmlSource);
    }
    setMode(newMode);
  };

  // Handle HTML source change
  const handleHtmlChange = (e) => {
    if (isProtectedMode) return;
    const newHtml = e.target.value;
    setHtmlSource(newHtml);
    onChange(sanitizeForEmail(newHtml));
  };

  // Handle template field change
  const handleTemplateFieldChange = (key, value) => {
    setTemplateFieldValues(prev => ({ ...prev, [key]: value }));
  };

  // Apply protected template
  const applyProtectedTemplate = (templateId) => {
    const template = PROTECTED_TEMPLATES[templateId];
    if (!template) return;
    
    if (template.isUnlocked) {
      // Custom/unlocked template - switch to free editing
      setIsProtectedMode(false);
      setSelectedTemplate(null);
      setTemplateFieldValues({});
      setHtmlSource('');
      lastGeneratedHtmlRef.current = '';
      onChange('');
      if (editor) editor.commands.setContent('');
    } else {
      // Protected template - switch to protected mode
      setSelectedTemplate(template);
      setIsProtectedMode(true);
      
      // Initialize field values with email
      const initialValues = {
        user_email: email || '',
      };
      setTemplateFieldValues(initialValues);
      
      const html = generateTemplateHtml(template, initialValues);
      lastGeneratedHtmlRef.current = html;
      setHtmlSource(html);
      onChange(html);
    }
  };

  // Apply legacy template (full HTML)
  const applyLegacyTemplate = (templateKey) => {
    const template = LEGACY_TEMPLATES[templateKey];
    if (template) {
      setIsProtectedMode(false);
      setSelectedTemplate(null);
      setHtmlSource(template.html);
      onChange(template.html);
      
      // For templates with table layouts, switch to HTML mode to preserve structure
      // TipTap doesn't preserve complex table layouts well in WYSIWYG mode
      if (template.html.includes('<table')) {
        setMode('html');
        toast.info('Switched to HTML mode', { description: 'Table-based layouts are best edited in HTML mode' });
      } else {
        if (editor) editor.commands.setContent(template.html);
      }
    }
  };

  // Unlock protected template (switch to full editing)
  const unlockTemplate = () => {
    setShowUnlockWarning(true);
  };

  const confirmUnlock = () => {
    setIsProtectedMode(false);
    setSelectedTemplate(null);
    // Keep the current HTML but allow full editing
    // If HTML contains tables, switch to HTML mode to preserve layout
    if (htmlSource.includes('<table')) {
      setMode('html');
      toast.info('Full editing enabled in HTML mode', { description: 'Table layouts are preserved in HTML mode' });
    } else {
      if (editor) editor.commands.setContent(htmlSource);
      toast.info('Full editing enabled', { description: 'You can now edit the entire signature' });
    }
    setShowUnlockWarning(false);
  };

  // Insert variable
  const insertVariable = (variable) => {
    if (isProtectedMode) return;
    if (mode === 'wysiwyg' && editor) {
      editor.chain().focus().insertContent(variable).run();
    } else {
      const textarea = document.querySelector(`#html-editor-${email?.replace(/[@.]/g, '-')}`);
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = htmlSource.substring(0, start) + variable + htmlSource.substring(end);
        setHtmlSource(newValue);
        onChange(sanitizeForEmail(newValue));
      }
    }
  };

  // Add link/image
  const addLink = () => {
    if (!editor || isProtectedMode) return;
    const url = window.prompt('Enter URL:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const addImage = () => {
    if (!editor || isProtectedMode) return;
    const url = window.prompt('Enter image URL:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  // Send test email
  const sendTestEmail = useCallback(async () => {
    if (!email) {
      toast.error('No email account selected');
      return;
    }

    setIsSendingTest(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let testSignature = htmlSource;
      Object.entries(mergedVariables).forEach(([key, val]) => {
        testSignature = testSignature.replace(new RegExp(`{{${key}}}`.replace(/[{}]/g, '\\$&'), 'g'), val || '');
      });
      // Handle conditional blocks
      testSignature = testSignature.replace(/\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, field, content) => {
        const value = mergedVariables[field];
        return value && value !== '' && value !== '#' ? content : '';
      });
      
      await axios.post(`${API}/gmail/send`, {
        to: email,
        subject: '✅ Email Signature Test - Alpine Armour',
        body_html: `<p style="font-family:Arial,sans-serif;font-size:14px;color:#333;">This is a test email to preview how your signature renders.</p><hr style="border:none;border-top:1px solid #e0e0e0;margin:16px 0;"/>${testSignature}`,
        from_account: email,
        cc: '',
        bcc: '',
        skip_signature: true,  // Don't append saved signature - we're testing a new one
      }, { headers });
      
      toast.success(`Test email sent to ${email}`, { description: 'Check your inbox to verify rendering' });
    } catch (error) {
      toast.error('Failed to send test email', { description: error.response?.data?.detail || error.message });
    } finally {
      setIsSendingTest(false);
    }
  }, [email, htmlSource, mergedVariables]);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Editor */}
      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-gray-50">
          {/* Mode Toggle */}
          <ModeToggle mode={mode} onModeChange={handleModeChange} disabled={isProtectedMode} />
          
          {isProtectedMode && (
            <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200 bg-blue-50 ml-2">
              <Shield className="w-3 h-3 mr-1" />Protected
            </Badge>
          )}
          
          <div className="w-px h-5 bg-gray-300 mx-2" />
          
          {/* Formatting buttons - only show in WYSIWYG mode and not protected */}
          {mode === 'wysiwyg' && !isProtectedMode && (
            <>
              <ToolbarButton onClick={() => editor?.chain().focus().toggleBold().run()} isActive={editor?.isActive('bold')} title="Bold" disabled={!editor}>
                <Bold className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={() => editor?.chain().focus().toggleItalic().run()} isActive={editor?.isActive('italic')} title="Italic" disabled={!editor}>
                <Italic className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={() => editor?.chain().focus().toggleUnderline().run()} isActive={editor?.isActive('underline')} title="Underline" disabled={!editor}>
                <UnderlineIcon className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={() => editor?.chain().focus().toggleStrike().run()} isActive={editor?.isActive('strike')} title="Strikethrough" disabled={!editor}>
                <Strikethrough className="w-4 h-4" />
              </ToolbarButton>

              <div className="w-px h-5 bg-gray-300 mx-1" />

              <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('left').run()} isActive={editor?.isActive({ textAlign: 'left' })} title="Align Left" disabled={!editor}>
                <AlignLeft className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('center').run()} isActive={editor?.isActive({ textAlign: 'center' })} title="Align Center" disabled={!editor}>
                <AlignCenter className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('right').run()} isActive={editor?.isActive({ textAlign: 'right' })} title="Align Right" disabled={!editor}>
                <AlignRight className="w-4 h-4" />
              </ToolbarButton>

              <div className="w-px h-5 bg-gray-300 mx-1" />

              <ToolbarButton onClick={addLink} isActive={editor?.isActive('link')} title="Add Link" disabled={!editor}>
                <LinkIcon className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={addImage} title="Add Image" disabled={!editor}>
                <ImageIcon className="w-4 h-4" />
              </ToolbarButton>

              <div className="w-px h-5 bg-gray-300 mx-1" />

              <ToolbarButton onClick={() => editor?.chain().focus().undo().run()} title="Undo" disabled={!editor}>
                <Undo className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton onClick={() => editor?.chain().focus().redo().run()} title="Redo" disabled={!editor}>
                <Redo className="w-4 h-4" />
              </ToolbarButton>
            </>
          )}

          <div className="flex-1" />

          {/* Variables dropdown - only when not protected */}
          {!isProtectedMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1">
                  <Variable className="w-3.5 h-3.5" />Variables
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">Insert Variable</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SIGNATURE_VARIABLES.map((v) => (
                  <DropdownMenuItem key={v.key} onClick={() => insertVariable(v.key)} className="text-xs">
                    <code className="text-blue-600 mr-2">{v.key}</code>
                    <span className="text-gray-500">{v.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Templates dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" data-testid="email-signature-templates-btn" onClick={(e) => e.stopPropagation()}>
                <LayoutTemplate className="w-3.5 h-3.5" />Templates
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs flex items-center gap-1">
                <Shield className="w-3 h-3 text-blue-600" />
                Brand Protected Templates
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {Object.entries(PROTECTED_TEMPLATES).map(([key, template]) => (
                <DropdownMenuItem key={key} onClick={() => applyProtectedTemplate(key)} className="flex flex-col items-start">
                  <div className="flex items-center gap-2 w-full">
                    {template.isUnlocked ? (
                      <Unlock className="w-3 h-3 text-gray-400" />
                    ) : (
                      <Lock className="w-3 h-3 text-blue-500" />
                    )}
                    <span className="text-sm font-medium">{template.name}</span>
                  </div>
                  <span className="text-xs text-gray-500 ml-5">{template.description}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-gray-400">Legacy Templates (Unlocked)</DropdownMenuLabel>
              {Object.entries(LEGACY_TEMPLATES).map(([key, template]) => (
                <DropdownMenuItem key={key} onClick={() => applyLegacyTemplate(key)} className="flex flex-col items-start">
                  <span className="text-sm">{template.name}</span>
                  <span className="text-xs text-gray-500">{template.description}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Editor content - Protected mode shows field editor, else shows WYSIWYG/HTML */}
        {isProtectedMode ? (
          <div className="p-4">
            <ProtectedTemplateEditor
              template={selectedTemplate}
              fieldValues={templateFieldValues}
              onFieldChange={handleTemplateFieldChange}
              onUnlock={unlockTemplate}
              businessSettings={businessSettings}
            />
          </div>
        ) : mode === 'wysiwyg' ? (
          <EditorContent editor={editor} />
        ) : (
          <textarea
            id={`html-editor-${email?.replace(/[@.]/g, '-')}`}
            value={htmlSource}
            onChange={handleHtmlChange}
            className="w-full min-h-[200px] p-3 font-mono text-xs bg-gray-900 text-green-400 focus:outline-none resize-y"
            placeholder="Enter email-safe HTML..."
            spellCheck={false}
          />
        )}

        {/* Footer */}
        <div className="px-3 py-2 bg-gray-50 border-t flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-500">{email}</span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={sendTestEmail} disabled={isSendingTest || !email} className="h-7 text-xs gap-1.5">
            {isSendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {isSendingTest ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>
      </div>

      {/* Live Preview */}
      {showPreview && (
        <div className="space-y-2">
          <PreviewModeToggle previewMode={previewMode} onModeChange={setPreviewMode} />
          <EmailPreview html={htmlSource} variables={mergedVariables} previewMode={previewMode} />
        </div>
      )}

      {/* Unlock Warning Dialog */}
      <AlertDialog open={showUnlockWarning} onOpenChange={setShowUnlockWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Unlock Full Editing?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">
                Switching to full editing mode will remove template protection. You will be able to edit the entire signature, including brand elements.
              </p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
                <strong>Warning:</strong> Changes to locked elements (logo, colors, layout) may break brand consistency.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Protected</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnlock} className="bg-amber-500 hover:bg-amber-600">
              <Unlock className="w-4 h-4 mr-2" />
              Unlock & Edit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor styles */}
      <style>{`
        .ProseMirror { min-height: 150px; font-family: Arial, sans-serif; font-size: 14px; }
        .ProseMirror p { margin: 0; }
        .ProseMirror p + p { margin-top: 0.5em; }
        .ProseMirror:focus { outline: none; }
        .ProseMirror a { color: #0066cc; text-decoration: none; }
        .ProseMirror table { border-collapse: collapse; width: auto; }
        .ProseMirror td, .ProseMirror th { vertical-align: top; }
      `}</style>
    </div>
  );
}

export { PROTECTED_TEMPLATES, LEGACY_TEMPLATES, SIGNATURE_VARIABLES, sanitizeForEmail, validateEmailHtml };
